import React, { useEffect, useRef, useState } from "react";
import type {
  Office,
  OfficeListItem,
  User,
  Config,
  ConfigVersion,
  Reference,
  FileRecord,
  Permission,
} from "../shared/contracts.ts";
import { permissions } from "../shared/contracts.ts";
import {
  api,
  date,
  money,
  bytes,
  fileUrl,
  Icon,
  Button,
  Banner,
  Badge,
  Empty,
  Field,
  Modal,
  tabKeys,
  permissionLabels,
} from "./ui.tsx";
import type { PublicIdentity, Notice, Integration } from "./ui.tsx";
import { FilePreview } from "./offices.tsx";
import { GoogleConnectionPanel } from "./google-connect.tsx";
export function Library({
  online,
  config,
  failed,
  notify,
}: {
  online: boolean;
  config: Config | null;
  failed: (e: any) => void;
  notify: (s: string) => void;
}) {
  const [refs, setRefs] = useState<Reference[] | null>(null),
    [versions, setVersions] = useState<Reference[]>([]),
    [query, setQuery] = useState(""),
    [filter, setFilter] = useState("active"),
    [modal, setModal] = useState(""),
    [selected, setSelected] = useState<Reference | null>(null),
    [busy, setBusy] = useState(false),
    [test, setTest] = useState<any>(null),
    [testRequest, setTestRequest] = useState(""),
    [testRecipient, setTestRecipient] = useState(""),
    [preview, setPreview] = useState<string | null>(null);
  const load = () => api<Reference[]>("/references").then(setRefs);
  useEffect(() => {
    load().catch(failed);
  }, []);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };
  const filtered = refs?.filter(
    (r) =>
      (filter === "all" ||
        (filter === "active" && r.active && !r.archived) ||
        (filter === "review" && r.processingState !== "ready") ||
        (filter === "inactive" && (!r.active || r.archived))) &&
      [r.name, r.category, r.description, ...r.tags]
        .join(" ")
        .toLocaleLowerCase("pt-BR")
        .includes(query.toLocaleLowerCase("pt-BR")),
  );
  return (
    <fieldset
      disabled={busy}
      aria-busy={busy}
      aria-label="Biblioteca de referências"
      style={{ border: 0, margin: 0, padding: 0, minWidth: 0, width: "100%" }}
    >
      {busy && (
        <Banner>
          Processando sua solicitação. Os campos ficam temporariamente
          bloqueados para preservar as alterações enviadas.
        </Banner>
      )}
      <div className="page-head">
        <div>
          <span className="eyebrow">ADMINISTRAÇÃO · REDAÇÃO</span>
          <h1>Biblioteca de referências</h1>
          <p>
            Estrutura e linguagem do gabinete, sem transportar fatos entre
            casos.
          </p>
        </div>
        <div className="head-actions">
          <Button
            icon="spark"
            disabled={!online}
            onClick={() => setModal("test")}
          >
            Testar redação
          </Button>
          <Button
            kind="primary"
            icon="plus"
            disabled={!online}
            onClick={() => {
              setSelected(null);
              setModal("reference");
            }}
          >
            Adicionar referência
          </Button>
        </div>
      </div>
      <Banner>
        Esta é a biblioteca da aplicação. Indexar referências não treina
        tecnicamente o modelo. A redação recebe somente os exemplos pertinentes
        e as orientações publicadas.
      </Banner>
      <section className="list-panel">
        <div
          className="tabs"
          role="tablist"
          onKeyDown={tabKeys}
          aria-label="Situação das referências"
        >
          {[
            ["active", "Ativas"],
            ["review", "Requerem conferência"],
            ["inactive", "Inativas e arquivadas"],
            ["all", "Todas"],
          ].map(([id, label]) => (
            <button
              role="tab"
              aria-selected={filter === id}
              key={id}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="list-toolbar">
          <div className="search">
            <Icon name="search" size={18} />
            <input
              aria-label="Pesquisar referências"
              placeholder="Buscar nome, categoria ou etiqueta"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <span>{filtered?.length ?? "…"} referências</span>
        </div>
        {refs === null ? (
          <div className="loading">
            <span className="spinner" />
            Consultando biblioteca…
          </div>
        ) : filtered?.length ? (
          <div className="reference-list">
            {filtered.map((r) => (
              <article className="reference-row" key={r.id}>
                <span className="reference-symbol">
                  <Icon name="library" size={23} />
                </span>
                <div className="reference-info">
                  <button
                    className="table-link"
                    onClick={() => {
                      setSelected(r);
                      setModal("reference");
                    }}
                  >
                    {r.name}
                  </button>
                  <p>{r.description || "Sem descrição."}</p>
                  <div className="reference-meta">
                    <span>{r.category || "Sem categoria"}</span>
                    <span>Versão {r.version}</span>
                    <span>
                      {r.sourceOfficeId
                        ? "Ofício protocolado"
                        : "Cadastro administrativo"}
                    </span>
                    {r.preferred && <span>★ Preferencial</span>}
                  </div>
                  <div className="tag-line">
                    {r.tags.map((t) => (
                      <span className="tag" key={t}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="reference-status">
                  <span
                    className={
                      "badge " +
                      (r.processingState === "ready" && r.active && !r.archived
                        ? "success"
                        : "warn")
                    }
                  >
                    {r.archived
                      ? "Arquivada"
                      : !r.active
                        ? "Inativa"
                        : r.processingState === "ready"
                          ? "Ativa · pronta para consulta"
                          : "Conferência necessária"}
                  </span>
                  <Button
                    kind="ghost"
                    icon="arrow"
                    aria-label={"Abrir " + r.name}
                    onClick={() => {
                      setSelected(r);
                      setModal("reference");
                    }}
                  />
                </div>
              </article>
            ))}
          </div>
        ) : (
          <Empty title="Nenhuma referência nesta seleção" icon="library">
            Adicione modelos ou textos administrativos. Os exemplos preparados a
            partir de ofícios confirmados aparecerão com sua origem
            identificada.
          </Empty>
        )}
      </section>
      {modal === "reference" && (
        <Modal
          title={selected ? "Referência de redação" : "Adicionar referência"}
          wide
          onClose={() => setModal("")}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              run(async () => {
                const payload = {
                  version: selected?.version,
                  name: String(f.get("name")),
                  category: String(f.get("category")),
                  recipientId: String(f.get("recipientId")),
                  description: String(f.get("description")),
                  origin: String(f.get("origin")),
                  text: String(f.get("text")),
                  tags: String(f.get("tags"))
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                  preferred: f.get("preferred") === "on",
                  active: f.get("active") === "on",
                };
                let ref = selected
                  ? await api<Reference>(
                      "/references/" + selected.id,
                      "PATCH",
                      payload,
                    )
                  : await api<Reference>("/references", "POST", payload);
                const file = f.get("file");
                if (file instanceof File && file.size) {
                  const data = new FormData();
                  data.set("file", file);
                  await api("/references/" + ref.id + "/file", "POST", data);
                }
                await load();
                setModal("");
                notify(
                  "Referência persistida. Confira o estado de preparação na biblioteca.",
                );
              });
            }}
          >
            <div className="field-grid">
              <Field label="Nome">
                <input
                  name="name"
                  defaultValue={selected?.name || ""}
                  required
                  autoFocus
                />
              </Field>
              <Field label="Categoria">
                <input
                  name="category"
                  defaultValue={selected?.category || ""}
                  required
                />
              </Field>
            </div>
            <div className="field-grid">
              <Field label="Secretaria ou destinatário relacionado">
                <select
                  name="recipientId"
                  defaultValue={selected?.recipientId || ""}
                >
                  <option value="">Referência geral</option>
                  {selected?.recipientId &&
                    !config?.recipients.some(
                      (r) => r.id === selected.recipientId,
                    ) && (
                      <option value={selected.recipientId}>
                        Destinatário removido ({selected.recipientId})
                      </option>
                    )}
                  {config?.recipients.map((r) => (
                    <option value={r.id} key={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Origem">
                <input
                  name="origin"
                  defaultValue={selected?.origin || "Cadastro administrativo"}
                  required
                />
              </Field>
            </div>
            <Field label="Descrição">
              <textarea
                name="description"
                defaultValue={selected?.description || ""}
                rows={2}
              />
            </Field>
            <Field label="Etiquetas" hint="Separe os assuntos por vírgulas.">
              <input
                name="tags"
                defaultValue={selected?.tags.join(", ") || ""}
              />
            </Field>
            <Field
              label="Texto utilizável"
              hint="Inclua estrutura e estilo. Não inclua dados pessoais de outros casos."
            >
              <textarea
                name="text"
                defaultValue={selected?.text || ""}
                rows={8}
              />
            </Field>
            <Field label="Modelo em PDF ou Word">
              <input name="file" type="file" accept=".pdf,.docx,.txt" />
            </Field>
            {selected?.fileId && (
              <Button
                type="button"
                icon="eye"
                onClick={() => setPreview(selected.fileId!)}
              >
                Consultar arquivo da referência
              </Button>
            )}
            <div className="checkbox-row">
              <label>
                <input
                  name="preferred"
                  type="checkbox"
                  defaultChecked={selected?.preferred || false}
                />
                Preferencial
              </label>
              <label>
                <input
                  name="active"
                  type="checkbox"
                  defaultChecked={selected?.active ?? true}
                />
                Ativa para consulta
              </label>
            </div>
            {selected && (
              <div className="reference-detail">
                <span className="badge neutral">Versão {selected.version}</span>
                <span>
                  Preparação:{" "}
                  {selected.processingState === "ready"
                    ? "Pronta"
                    : selected.processingState === "needs_review"
                      ? "Requer conferência"
                      : "Falhou"}
                </span>
                <small>ID {selected.id}</small>
                {selected.sourceOfficeId && (
                  <small>
                    Origem automática: ofício {selected.sourceOfficeId}
                  </small>
                )}
                <details>
                  <summary>Versões desta referência</summary>
                  <Button
                    type="button"
                    disabled={!online || busy}
                    onClick={() =>
                      run(async () =>
                        setVersions(
                          await api<Reference[]>(
                            "/references/" + selected.id + "/versions",
                          ),
                        ),
                      )
                    }
                  >
                    Consultar versões
                  </Button>
                  {versions
                    .filter((v) => v.id === selected.id)
                    .map((v) => (
                      <article key={v.version}>
                        <strong>
                          Versão {v.version} · {date(v.updatedAt)}
                        </strong>
                        <p>
                          {v.active ? "Ativa" : "Inativa"} ·{" "}
                          {v.processingState === "ready"
                            ? "Pronta"
                            : "Requer conferência"}
                        </p>
                        <pre className="diff-text">
                          {v.text || "Conteúdo retirado desta versão."}
                        </pre>
                      </article>
                    ))}
                </details>
              </div>
            )}
            <div className="modal-actions split">
              {selected && (
                <div className="reference-lifecycle">
                  <Button
                    type="button"
                    disabled={!online || busy}
                    onClick={() =>
                      run(async () => {
                        await api("/references/" + selected.id, "PATCH", {
                          action: selected.active ? "deactivate" : "activate",
                        });
                        await load();
                        setModal("");
                        notify("Situação da referência atualizada.");
                      })
                    }
                  >
                    {selected.active ? "Desativar" : "Ativar"}
                  </Button>
                  <Button
                    type="button"
                    disabled={!online || busy || selected.archived}
                    onClick={() =>
                      run(async () => {
                        await api("/references/" + selected.id, "PATCH", {
                          action: "archive",
                        });
                        await load();
                        setModal("");
                        notify("Referência arquivada.");
                      })
                    }
                  >
                    Arquivar
                  </Button>
                  <Button
                    kind="danger-ghost"
                    type="button"
                    disabled={!online || busy}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Retirar esta referência das próximas consultas?",
                        )
                      )
                        run(async () => {
                          await api("/references/" + selected.id, "PATCH", {
                            action: "remove",
                          });
                          await load();
                          setModal("");
                          notify("Referência retirada das próximas consultas.");
                        });
                    }}
                  >
                    Retirar
                  </Button>
                </div>
              )}
              <div>
                <Button type="button" onClick={() => setModal("")}>
                  Cancelar
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  busy={busy}
                  disabled={!online}
                >
                  Salvar referência
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      )}
      {modal === "test" && (
        <Modal
          title="Teste de redação com pedido fictício"
          wide
          onClose={() => setModal("")}
        >
          <Banner>
            Este teste usa a API e pode gerar consumo. Ele não numera ofício,
            não escreve na planilha, não protocola, não notifica a produção e
            não concede recompensa.
          </Banner>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              run(async () =>
                setTest(
                  await api("/references/test", "POST", {
                    request: testRequest,
                    recipientId: testRecipient,
                  }),
                ),
              );
            }}
          >
            <Field label="Pedido fictício">
              <textarea
                required
                rows={4}
                value={testRequest}
                onChange={(e) => setTestRequest(e.target.value)}
                placeholder="Use uma situação fictícia, sem dados pessoais reais."
              />
            </Field>
            <Field label="Destinatário">
              <select
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
              >
                <option value="">Selecione</option>
                {config?.recipients
                  .filter((r) => r.active)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Button
              kind="primary"
              type="submit"
              busy={busy}
              disabled={!online || !config?.ai.enabled}
            >
              Gerar teste com os limites atuais
            </Button>
          </form>
          {test && (
            <section className="test-result">
              <h3>Resultado do teste</h3>
              <pre>
                {test.text ||
                  test.result?.text ||
                  test.output?.text ||
                  JSON.stringify(test, null, 2)}
              </pre>
              {test.missing?.length > 0 && (
                <Banner kind="warn">
                  Informações pendentes na redação: {test.missing.join("; ")}
                </Banner>
              )}
              {typeof test.costCents === "number" && (
                <p>Consumo identificado: {money(test.costCents)}</p>
              )}
              {(test.references || test.selectedReferences) && (
                <details open>
                  <summary>Referências selecionadas</summary>
                  <pre>
                    {JSON.stringify(
                      test.references || test.selectedReferences,
                      null,
                      2,
                    )}
                  </pre>
                </details>
              )}
            </section>
          )}
        </Modal>
      )}
      {preview && (
        <FilePreview
          id={preview}
          onClose={() => setPreview(null)}
          failed={failed}
        />
      )}
    </fieldset>
  );
}
export function Users({
  online,
  failed,
  notify,
}: {
  online: boolean;
  failed: (e: any) => void;
  notify: (s: string) => void;
}) {
  const [users, setUsers] = useState<User[] | null>(null),
    [editing, setEditing] = useState<User | null | undefined>(undefined),
    [busy, setBusy] = useState(false);
  const load = () => api<User[]>("/users").then(setUsers);
  useEffect(() => {
    load().catch(failed);
  }, []);
  return (
    <fieldset
      disabled={busy}
      aria-busy={busy}
      aria-label="Pessoas e permissões"
      style={{ border: 0, margin: 0, padding: 0, minWidth: 0, width: "100%" }}
    >
      {busy && (
        <Banner>
          Processando sua solicitação. Os campos ficam temporariamente
          bloqueados para preservar as alterações enviadas.
        </Banner>
      )}
      <div className="page-head">
        <div>
          <span className="eyebrow">ADMINISTRAÇÃO · ACESSO</span>
          <h1>Pessoas e permissões</h1>
          <p>
            Cada ação possui autorização própria, conferida também no servidor.
          </p>
        </div>
        <Button
          kind="primary"
          icon="plus"
          disabled={!online}
          onClick={() => setEditing(null)}
        >
          Adicionar pessoa
        </Button>
      </div>
      <section className="list-panel">
        {users === null ? (
          <div className="loading">
            <span className="spinner" />
            Consultando pessoas…
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>Acesso</th>
                <th>Permissões de ofícios</th>
                <th>
                  <span className="sr-only">Editar</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.name}</strong>
                    <p>{u.phone}</p>
                  </td>
                  <td>
                    <span
                      className={"badge " + (u.active ? "success" : "neutral")}
                    >
                      {u.active ? "Ativo" : "Inativo"}
                    </span>
                    {u.admin && <p>Administrador</p>}
                  </td>
                  <td>
                    {u.admin
                      ? "Administração completa"
                      : u.permissions
                          .map((p) => permissionLabels[p])
                          .join(" · ") || "Sem permissões concedidas"}
                  </td>
                  <td>
                    <Button kind="ghost" onClick={() => setEditing(u)}>
                      Editar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {editing !== undefined && (
        <Modal
          title={editing ? "Editar pessoa" : "Adicionar pessoa"}
          onClose={() => setEditing(undefined)}
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              const f = new FormData(e.currentTarget);
              try {
                const updated = await api<User & { reauthenticate?: boolean }>(
                  "/users",
                  "POST",
                  {
                    id: editing?.id,
                    name: String(f.get("name")),
                    phone: String(f.get("phone")),
                    password: String(f.get("password")) || undefined,
                    admin: f.get("admin") === "on",
                    active: f.get("active") === "on",
                    permissions: permissions.filter((p) => f.get(p) === "on"),
                  },
                );
                if (updated.reauthenticate) {
                  window.location.href = "/?access_updated=1";
                  return;
                }
                await load();
                setEditing(undefined);
                notify("Pessoa e permissões atualizadas no servidor.");
              } catch (e) {
                failed(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Field label="Nome">
              <input name="name" defaultValue={editing?.name || ""} required />
            </Field>
            <Field label="Telefone de acesso">
              <input
                name="phone"
                type="tel"
                defaultValue={editing?.phone || ""}
                required
              />
            </Field>
            <Field
              label={
                editing
                  ? "Nova senha (deixe vazio para preservar)"
                  : "Senha inicial"
              }
            >
              <input
                name="password"
                type="password"
                autoComplete="new-password"
                required={!editing}
                minLength={12}
              />
            </Field>
            <div className="permissions-grid">
              <label>
                <input
                  name="admin"
                  type="checkbox"
                  defaultChecked={editing?.admin || false}
                />
                Administrador
              </label>
              <label>
                <input
                  name="active"
                  type="checkbox"
                  defaultChecked={editing?.active ?? true}
                />
                Conta ativa
              </label>
            </div>
            <h3>Permissões independentes</h3>
            <div className="permissions-grid">
              {permissions.map((p) => (
                <label key={p}>
                  <input
                    name={p}
                    type="checkbox"
                    defaultChecked={editing?.permissions.includes(p) || false}
                  />
                  {permissionLabels[p]}
                </label>
              ))}
            </div>
            <p className="muted">
              As ativações administrativas da IA e do Solar continuam
              independentes dessas permissões.
            </p>
            <div className="modal-actions">
              <Button type="button" onClick={() => setEditing(undefined)}>
                Cancelar
              </Button>
              <Button
                kind="primary"
                type="submit"
                busy={busy}
                disabled={!online}
              >
                Salvar acesso
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </fieldset>
  );
}
export function Configuration({
  integrations,
  online,
  failed,
  notify,
  onDirty,
  onPublished,
}: {
  integrations: Integration[];
  online: boolean;
  failed: (e: any) => void;
  notify: (s: string) => void;
  onDirty: (d: boolean) => void;
  onPublished: () => void;
}) {
  const [state, setState] = useState<{
      published: ConfigVersion;
      draft?: ConfigVersion;
    } | null>(null),
    [value, setValue] = useState<Config | null>(null),
    [tab, setTab] = useState("ai"),
    [dirty, setDirty] = useState(false),
    [busy, setBusy] = useState(""),
    [query, setQuery] = useState(""),
    [modal, setModal] = useState(""),
    [advanced, setAdvanced] = useState(""),
    [advancedError, setAdvancedError] = useState(""),
    [advancedDirty, setAdvancedDirty] = useState(false),
    [restoreVersion, setRestoreVersion] = useState(""),
    [versions, setVersions] = useState<ConfigVersion[]>([]),
    [usage, setUsage] = useState<{
      month: string;
      spent: number;
      reserved: number;
      limit: number;
      uncertain?: number;
    } | null>(null),
    [simulation, setSimulation] = useState<{
      valid: boolean;
      issues: string[];
      effects: string[];
    } | null>(null),
    [recipientNotice, setRecipientNotice] = useState("");
  const load = async () => {
    const next = await api("/config");
    setState(next);
    setValue(structuredClone((next.draft || next.published).config));
    setAdvanced(JSON.stringify((next.draft || next.published).config, null, 2));
    setAdvancedDirty(false);
    setRecipientNotice("");
    setDirty(false);
    onDirty(false);
    await Promise.all([
      api<ConfigVersion[]>("/config/versions").then(setVersions),
      api("/usage").then(setUsage),
    ]);
  };
  useEffect(() => {
    load().catch(failed);
    return () => onDirty(false);
  }, []);
  const change = (section: keyof Config, key: string, newValue: any) => {
    setValue((v) =>
      v ? { ...v, [section]: { ...(v[section] as any), [key]: newValue } } : v,
    );
    setDirty(true);
    onDirty(true);
  };
  const run = async (name: string, fn: () => Promise<void>) => {
    setBusy(name);
    try {
      await fn();
    } catch (e) {
      failed(e);
    } finally {
      setBusy("");
    }
  };
  if (!state || !value)
    return (
      <div className="loading">
        <span className="spinner" />
        Carregando configurações…
      </div>
    );
  const tabs = [
    [
      "ai",
      "Redação e consumo",
      "modelo orçamento instruções obrigatórios geração",
    ],
    ["google", "Drive e planilha", "pastas colunas cabeçalhos numeração"],
    ["solar", "Protocolo e andamento", "bot consulta ativação retentativas"],
    ["recipients", "Destinatários", "secretarias órgãos"],
    ["layout", "Documento e timbre", "margens cabeçalho rodapé fonte"],
    ["identity", "Identidade visual", "logo cores tema"],
    ["limits", "Arquivos e limites", "tamanho anexos"],
    ["rewards", "Recompensas", "pontos xp concluidor"],
    ["versions", "Versões e publicação", "restaurar comparação rascunho"],
    ["advanced", "Configuração completa", "json"],
  ];
  return (
    <fieldset
      disabled={!!busy}
      aria-busy={!!busy}
      aria-label="Configurações de ofícios"
      style={{ border: 0, margin: 0, padding: 0, minWidth: 0, width: "100%" }}
    >
      {busy && (
        <Banner>
          Processando sua solicitação. Os campos ficam temporariamente
          bloqueados para preservar as alterações enviadas.
        </Banner>
      )}
      <div className="page-head">
        <div>
          <span className="eyebrow">ADMINISTRAÇÃO · CONFIGURAÇÕES</span>
          <h1>Configurações de ofícios</h1>
          <p>Prepare, confira o impacto e publique uma versão consistente.</p>
        </div>
        <div className="head-actions">
          <Button
            icon="eye"
            busy={busy === "simulate"}
            disabled={!online || !!busy || advancedDirty}
            onClick={() =>
              run("simulate", async () => {
                setSimulation(
                  await api("/config/simulate", "POST", { config: value }),
                );
                setModal("preview");
              })
            }
          >
            Prévia e simulação
          </Button>
          <Button
            busy={busy === "save"}
            disabled={!dirty || advancedDirty || !online || !!busy}
            onClick={() =>
              run("save", async () => {
                await api("/config", "PUT", {
                  config: value,
                  baseVersion: state.published.version,
                });
                await load();
                notify("Rascunho da configuração salvo no servidor.");
              })
            }
          >
            Salvar rascunho
          </Button>
          <Button
            kind="primary"
            disabled={dirty || !state.draft || !online || !!busy}
            onClick={() => setModal("publish")}
          >
            Revisar publicação
          </Button>
        </div>
      </div>
      <div className="config-version">
        <span className="badge success">
          Publicada · versão {state.published.version}
        </span>
        {state.draft && (
          <span className="badge warn">
            Rascunho · versão {state.draft.version}
          </span>
        )}
        {dirty && (
          <span className="unsaved">
            Alterações nesta tela ainda não salvas
          </span>
        )}
      </div>
      {advancedDirty && (
        <Banner kind="warn">
          O JSON foi editado e ainda não foi aplicado à configuração. Use
          “Aplicar JSON à edição” antes de salvar ou simular. O conteúdo
          permanece nesta tela ao alternar os assuntos.
        </Banner>
      )}
      <div className="settings-grid">
        <aside className="settings-nav">
          <div className="search">
            <Icon name="search" size={17} />
            <input
              aria-label="Pesquisar configurações"
              placeholder="Pesquisar configurações"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <nav aria-label="Assuntos de configuração">
            {tabs
              .filter((t) =>
                t
                  .join(" ")
                  .toLocaleLowerCase("pt-BR")
                  .includes(query.toLocaleLowerCase("pt-BR")),
              )
              .map(([key, label]) => (
                <button
                  key={key}
                  className={tab === key ? "active" : ""}
                  aria-current={tab === key ? "page" : undefined}
                  onClick={() => {
                    if (key === "advanced" && !advancedDirty)
                      setAdvanced(JSON.stringify(value, null, 2));
                    setTab(key);
                  }}
                >
                  {label}
                  <Icon name="arrow" size={16} />
                </button>
              ))}
          </nav>
        </aside>
        <section className="settings-form">
          <div className="section-head">
            <div>
              <h2>{tabs.find((t) => t[0] === tab)?.[1]}</h2>
              <p>
                Alcance: módulo de ofícios. As alterações só entram em vigor
                após a publicação.
              </p>
            </div>
          </div>
          {tab === "ai" && (
            <>
              {usage && (
                <div className="usage-summary">
                  <span>
                    <small>Consumo apurado · {usage.month}</small>
                    <strong>{money(usage.spent)}</strong>
                  </span>
                  <span>
                    <small>Reserva em processamento</small>
                    <strong>{money(usage.reserved)}</strong>
                    {!!usage.uncertain && (
                      <small>
                        Montante aguardando conferência:{" "}
                        {money(usage.uncertain)}
                      </small>
                    )}
                  </span>
                  <span>
                    <small>Limite publicado</small>
                    <strong>{money(usage.limit)}</strong>
                  </span>
                </div>
              )}
              <Toggle
                label="Ativar redação com IA"
                checked={value.ai.enabled}
                onChange={(v) => change("ai", "enabled", v)}
              />
              <p className="muted">
                A permissão de cada pessoa continua necessária. Credenciais são
                configuradas no servidor, fora desta tela.
              </p>
              <Field label="Modelo da API">
                <input
                  value={value.ai.model}
                  onChange={(e) => change("ai", "model", e.target.value)}
                  placeholder="Identificador exato do modelo configurado"
                />
              </Field>
              <div className="field-grid">
                <NumberField
                  label="Orçamento mensal (centavos de R$)"
                  value={value.ai.monthlyBudgetCents}
                  onChange={(v) => change("ai", "monthlyBudgetCents", v)}
                />
                <NumberField
                  label="Limite por solicitação (centavos de R$)"
                  value={value.ai.maxRequestCents}
                  onChange={(v) => change("ai", "maxRequestCents", v)}
                />
              </div>
              <div className="field-grid">
                <NumberField
                  label="Custo por milhão de tokens de entrada (centavos de R$)"
                  value={value.ai.inputCentsPerMillion}
                  onChange={(v) => change("ai", "inputCentsPerMillion", v)}
                />
                <NumberField
                  label="Custo por milhão de tokens de saída (centavos de R$)"
                  value={value.ai.outputCentsPerMillion}
                  onChange={(v) => change("ai", "outputCentsPerMillion", v)}
                />
              </div>
              <div className="field-grid">
                <NumberField
                  label="Máximo de caracteres de entrada"
                  value={value.ai.maxInputChars}
                  onChange={(v) => change("ai", "maxInputChars", v)}
                />
                <NumberField
                  label="Máximo de tokens de saída"
                  value={value.ai.maxOutputTokens}
                  onChange={(v) => change("ai", "maxOutputTokens", v)}
                />
              </div>
              <Banner kind="warn">
                Preencha os custos atuais do provedor e a conversão aprovada.
                Valores não verificados não representam uma franquia gratuita.
              </Banner>
              <Field label="Orientações de linguagem do gabinete">
                <textarea
                  rows={8}
                  value={value.ai.instructions}
                  onChange={(e) => change("ai", "instructions", e.target.value)}
                />
              </Field>
              <Field
                label="Campos essenciais"
                hint="Identificadores separados por vírgulas. Exemplos de campos existentes: request, subject, recipientId, requesterName, requesterPhone."
              >
                <input
                  value={value.ai.requiredFields.join(", ")}
                  onChange={(e) =>
                    change(
                      "ai",
                      "requiredFields",
                      e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    )
                  }
                />
              </Field>
            </>
          )}
          {tab === "google" && (
            <>
              <GoogleConnectionPanel />
              <Toggle
                label="Ativar integração com Google"
                checked={value.google.enabled}
                onChange={(v) => change("google", "enabled", v)}
              />
              <p className="muted">
                A conexão autorizada e as credenciais ficam no servidor. Salvar
                IDs não concede acesso aos arquivos.
              </p>
              {[
                ["spreadsheetId", "Identificador da planilha"],
                ["annualSheetPattern", "Padrão do nome da aba anual"],
                ["pdfFolderId", "Pasta de PDFs finais"],
                ["attachmentFolderId", "Pasta de anexos"],
                ["receiptFolderId", "Pasta de comprovantes"],
                ["referenceFolderId", "Pasta de referências"],
              ].map(([key, label]) => (
                <Field
                  key={key}
                  label={label}
                  hint={
                    key === "annualSheetPattern"
                      ? "Use {year} para o ano da emissão."
                      : undefined
                  }
                >
                  <input
                    value={(value.google as any)[key]}
                    onChange={(e) => change("google", key, e.target.value)}
                  />
                </Field>
              ))}
              <Toggle
                label="Sequência externa conferida e coordenação das emissões autorizada"
                checked={value.google.externalNumberingConfirmed}
                onChange={(v) =>
                  change("google", "externalNumberingConfirmed", v)
                }
              />
              <Banner kind="warn">
                As colunas de acompanhamento e as colunas sem definição devem
                permanecer preservadas. O servidor confere os cabeçalhos antes
                de escrever.
              </Banner>
              <ObjectFields
                title="Mapeamento de campos para colunas"
                value={value.google.mapping}
                onChange={(v) => change("google", "mapping", v)}
                hint="Defina os campos operacionais e as colunas explicitamente autorizadas."
              />
              <ObjectFields
                title="Cabeçalhos esperados"
                value={value.google.expectedHeaders}
                onChange={(v) => change("google", "expectedHeaders", v)}
                hint="Cabeçalho observado para cada coluna mapeada."
              />
            </>
          )}
          {tab === "solar" && (
            <>
              <Toggle
                label="Ativar protocolo no Solar"
                checked={value.solar.enabled}
                onChange={(v) => change("solar", "enabled", v)}
              />
              <Banner kind="warn">
                A ativação não substitui a verificação técnica do fluxo real. O
                sistema deve bloquear o envio quando o fluxo, a sessão ou a
                assinatura ainda não estiverem comprovados.
              </Banner>
              <Toggle
                label="Fluxo real verificado para operação"
                checked={value.solar.workflowVerified}
                onChange={(v) => change("solar", "workflowVerified", v)}
              />
              <NumberField
                label="Intervalo de consulta de movimentações (minutos)"
                value={value.solar.pollMinutes}
                min={1}
                onChange={(v) => change("solar", "pollMinutes", v)}
              />
              <div className="integration-list">
                {integrations
                  .filter((i) => /solar/i.test(i.id + " " + i.name))
                  .map((i) => (
                    <Banner key={i.id} kind={i.ready ? "success" : "warn"}>
                      <strong>{i.name}</strong>
                      <p>{i.message}</p>
                    </Banner>
                  ))}
              </div>
              <p className="muted">
                A conta única do gabinete e sua sessão são conectadas em
                ambiente protegido no servidor. Senhas e cookies não devem ser
                inseridos em configurações exportáveis.
              </p>
            </>
          )}
          {tab === "recipients" && (
            <>
              <p className="muted">
                Os destinatários cadastrados são escolhidos pelo usuário. A IA e
                o bot não os decidem.
              </p>
              {recipientNotice && <Banner>{recipientNotice}</Banner>}
              {!value.recipients.length && (
                <p className="muted">
                  Nenhum destinatário nesta edição. Use “Adicionar destinatário”
                  para cadastrar um.
                </p>
              )}
              {value.recipients.map((r, index) => (
                <div className="recipient-edit" key={index}>
                  <div className="field-grid">
                    <Field label="Identificador">
                      <input
                        value={r.id}
                        onChange={(e) => {
                          const list = [...value.recipients];
                          list[index] = { ...r, id: e.target.value };
                          setValue({ ...value, recipients: list });
                          setDirty(true);
                          onDirty(true);
                        }}
                      />
                    </Field>
                    <Field label="Nome">
                      <input
                        value={r.name}
                        onChange={(e) => {
                          const list = [...value.recipients];
                          list[index] = { ...r, name: e.target.value };
                          setValue({ ...value, recipients: list });
                          setDirty(true);
                          onDirty(true);
                        }}
                      />
                    </Field>
                  </div>
                  <Field label="Descrição">
                    <input
                      value={r.description || ""}
                      onChange={(e) => {
                        const list = [...value.recipients];
                        list[index] = { ...r, description: e.target.value };
                        setValue({ ...value, recipients: list });
                        setDirty(true);
                        onDirty(true);
                      }}
                    />
                  </Field>
                  <Toggle
                    label="Destinatário ativo"
                    checked={r.active}
                    onChange={(active) => {
                      const list = [...value.recipients];
                      list[index] = { ...r, active };
                      setValue({ ...value, recipients: list });
                      setDirty(true);
                      onDirty(true);
                    }}
                  />
                  <div className="recipient-actions">
                    <Button
                      type="button"
                      kind="danger-ghost"
                      aria-label={`Excluir destinatário ${r.name.trim() || r.id.trim() || `sem nome, item ${index + 1}`}`}
                      onClick={() => {
                        setValue((current) =>
                          current
                            ? {
                                ...current,
                                recipients: current.recipients.filter(
                                  (_, itemIndex) => itemIndex !== index,
                                ),
                              }
                            : current,
                        );
                        setDirty(true);
                        onDirty(true);
                        setRecipientNotice(
                          "Destinatário excluído desta edição. Salve o rascunho e publique para aplicar a exclusão. Os ofícios existentes são preservados.",
                        );
                      }}
                    >
                      Excluir destinatário
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                icon="plus"
                onClick={() => {
                  setValue({
                    ...value,
                    recipients: [
                      ...value.recipients,
                      { id: "", name: "", description: "", active: true },
                    ],
                  });
                  setDirty(true);
                  onDirty(true);
                }}
              >
                Adicionar destinatário
              </Button>
            </>
          )}
          {tab === "layout" && (
            <>
              <Toggle
                label="Acrescentar imagens ao mesmo PDF do ofício"
                checked={value.layout.appendImages !== false}
                onChange={(checked) =>
                  change("layout", "appendImages", checked)
                }
              />
              <p className="muted">
                Cada imagem ocupa uma página após o texto, com proporção
                preservada. O usuário define a ordem no rascunho. Os demais
                formatos permanecem como arquivos complementares. A alteração
                vale após a publicação e preserva os PDFs já finalizados.
              </p>
              <Field label="Texto do cabeçalho">
                <textarea
                  rows={3}
                  value={value.layout.heading}
                  onChange={(e) => change("layout", "heading", e.target.value)}
                />
              </Field>
              <Field label="Texto do rodapé">
                <textarea
                  rows={3}
                  value={value.layout.footer}
                  onChange={(e) => change("layout", "footer", e.target.value)}
                />
              </Field>
              <div className="field-grid">
                <NumberField
                  label="Margem (mm)"
                  value={value.layout.marginMm}
                  min={10}
                  max={40}
                  onChange={(v) => change("layout", "marginMm", v)}
                />
                <NumberField
                  label="Tamanho do texto (pt)"
                  value={value.layout.fontSize}
                  min={10}
                  max={16}
                  onChange={(v) => change("layout", "fontSize", v)}
                />
              </div>
              <AssetUpload
                label="Enviar imagem do cabeçalho"
                value={value.layout.headerFileId}
                online={online}
                onBusyChange={(isBusy) => setBusy(isBusy ? "asset-upload" : "")}
                onUploaded={(id) => change("layout", "headerFileId", id)}
                failed={failed}
              />
              <Field label="Identificador do arquivo de cabeçalho">
                <input
                  value={value.layout.headerFileId || ""}
                  onChange={(e) =>
                    change(
                      "layout",
                      "headerFileId",
                      e.target.value || undefined,
                    )
                  }
                />
              </Field>
              <Toggle
                label="Posicionar cabeçalho na página"
                checked={!!value.layout.headerPlacement}
                onChange={(checked) =>
                  change(
                    "layout",
                    "headerPlacement",
                    checked
                      ? { xMm: 0, topMm: 0, widthMm: 210, maxHeightMm: 41.02 }
                      : undefined,
                  )
                }
              />
              {value.layout.headerPlacement ? (
                <div className="field-grid">
                  <NumberField
                    label="Cabeçalho: distância da esquerda (mm)"
                    value={value.layout.headerPlacement.xMm}
                    max={210}
                    step="any"
                    onChange={(xMm) =>
                      change("layout", "headerPlacement", {
                        ...value.layout.headerPlacement!,
                        xMm,
                      })
                    }
                  />
                  <NumberField
                    label="Cabeçalho: distância do topo (mm)"
                    value={value.layout.headerPlacement.topMm}
                    max={297}
                    step="any"
                    onChange={(topMm) =>
                      change("layout", "headerPlacement", {
                        ...value.layout.headerPlacement!,
                        topMm,
                      })
                    }
                  />
                  <NumberField
                    label="Largura do cabeçalho (mm)"
                    value={value.layout.headerPlacement.widthMm}
                    min={0.01}
                    max={210}
                    step="any"
                    onChange={(widthMm) =>
                      change("layout", "headerPlacement", {
                        ...value.layout.headerPlacement!,
                        widthMm,
                      })
                    }
                  />
                  <NumberField
                    label="Altura máxima do cabeçalho (mm)"
                    value={value.layout.headerPlacement.maxHeightMm}
                    min={0.01}
                    max={100}
                    step="any"
                    onChange={(maxHeightMm) =>
                      change("layout", "headerPlacement", {
                        ...value.layout.headerPlacement!,
                        maxHeightMm,
                      })
                    }
                  />
                </div>
              ) : (
                <p className="muted">
                  O cabeçalho se ajusta à área entre as margens do documento.
                </p>
              )}
              <AssetUpload
                label="Enviar imagem do rodapé"
                value={value.layout.footerFileId}
                online={online}
                onBusyChange={(isBusy) => setBusy(isBusy ? "asset-upload" : "")}
                onUploaded={(id) => change("layout", "footerFileId", id)}
                failed={failed}
              />
              <Field label="Identificador do arquivo de rodapé">
                <input
                  value={value.layout.footerFileId || ""}
                  onChange={(e) =>
                    change(
                      "layout",
                      "footerFileId",
                      e.target.value || undefined,
                    )
                  }
                />
              </Field>
              <Toggle
                label="Posicionar rodapé na página"
                checked={!!value.layout.footerPlacement}
                onChange={(checked) =>
                  change(
                    "layout",
                    "footerPlacement",
                    checked
                      ? {
                          xMm: 17.7,
                          bottomMm: 5.8,
                          widthMm: 178.63,
                          maxHeightMm: 13.24,
                        }
                      : undefined,
                  )
                }
              />
              {value.layout.footerPlacement ? (
                <div className="field-grid">
                  <NumberField
                    label="Rodapé: distância da esquerda (mm)"
                    value={value.layout.footerPlacement.xMm}
                    max={210}
                    step="any"
                    onChange={(xMm) =>
                      change("layout", "footerPlacement", {
                        ...value.layout.footerPlacement!,
                        xMm,
                      })
                    }
                  />
                  <NumberField
                    label="Rodapé: distância da base (mm)"
                    value={value.layout.footerPlacement.bottomMm}
                    max={297}
                    step="any"
                    onChange={(bottomMm) =>
                      change("layout", "footerPlacement", {
                        ...value.layout.footerPlacement!,
                        bottomMm,
                      })
                    }
                  />
                  <NumberField
                    label="Largura do rodapé (mm)"
                    value={value.layout.footerPlacement.widthMm}
                    min={0.01}
                    max={210}
                    step="any"
                    onChange={(widthMm) =>
                      change("layout", "footerPlacement", {
                        ...value.layout.footerPlacement!,
                        widthMm,
                      })
                    }
                  />
                  <NumberField
                    label="Altura máxima do rodapé (mm)"
                    value={value.layout.footerPlacement.maxHeightMm}
                    min={0.01}
                    max={100}
                    step="any"
                    onChange={(maxHeightMm) =>
                      change("layout", "footerPlacement", {
                        ...value.layout.footerPlacement!,
                        maxHeightMm,
                      })
                    }
                  />
                </div>
              ) : (
                <p className="muted">
                  O rodapé se ajusta à área entre as margens do documento.
                </p>
              )}
              <p className="muted">
                Use arquivos autorizados já armazenados no sistema. A composição
                preserva proporção, margens e texto. A prévia de configuração é
                indicativa; confira o PDF gerado antes do envio.
              </p>
            </>
          )}
          {tab === "identity" && (
            <>
              <Field label="Nome do sistema">
                <input
                  value={value.identity.name}
                  onChange={(e) => change("identity", "name", e.target.value)}
                />
              </Field>
              <AssetUpload
                label="Enviar logo do gabinete"
                value={value.identity.logoFileId}
                online={online}
                onBusyChange={(isBusy) => setBusy(isBusy ? "asset-upload" : "")}
                onUploaded={(id) => change("identity", "logoFileId", id)}
                failed={failed}
              />
              <Field label="Identificador da logo">
                <input
                  value={value.identity.logoFileId || ""}
                  onChange={(e) =>
                    change(
                      "identity",
                      "logoFileId",
                      e.target.value || undefined,
                    )
                  }
                />
              </Field>
              <Field label="Tema padrão">
                <select
                  value={value.identity.theme}
                  onChange={(e) => change("identity", "theme", e.target.value)}
                >
                  <option value="light">Claro</option>
                  <option value="dark">Escuro</option>
                </select>
              </Field>
              <ObjectFields
                title="Tokens de cor personalizados"
                value={value.identity.colors}
                onChange={(v) => change("identity", "colors", v)}
                hint="Use os nomes dos tokens compartilhados, como brand, accent, background e ink. Valide o contraste na prévia antes de publicar."
              />
            </>
          )}
          {tab === "limits" && (
            <>
              <NumberField
                label="Tamanho máximo por arquivo (MB)"
                value={value.limits.maxUploadMb}
                min={1}
                max={20}
                onChange={(v) => change("limits", "maxUploadMb", v)}
              />
              <NumberField
                label="Quantidade máxima de anexos por ofício"
                value={value.limits.maxFiles}
                min={1}
                max={100}
                onChange={(v) => change("limits", "maxFiles", v)}
              />
              <p className="muted">
                O servidor aplica estes limites. Falha no envio de um arquivo
                não deve apagar o rascunho.
              </p>
            </>
          )}
          {tab === "rewards" && (
            <>
              <Banner>
                Um ofício confirmado no Solar conta uma vez como demanda
                encerrada. Compartilhamento não divide pontos e XP; a recompensa
                pertence somente ao concluidor definido na política publicada.
              </Banner>
              <Field label="Política do concluidor">
                <select
                  value={value.rewards.conclusionPolicy}
                  onChange={(e) =>
                    change("rewards", "conclusionPolicy", e.target.value)
                  }
                >
                  <option value="pending">Decisão de negócio pendente</option>
                  <option value="confirming_operator">
                    Operador que confirmou o envio
                  </option>
                </select>
              </Field>
              <div className="field-grid">
                <Field label="Pontos por ofício protocolado">
                  <input
                    type="number"
                    min={0}
                    value={value.rewards.points ?? ""}
                    placeholder="Ainda não definido"
                    onChange={(e) =>
                      change(
                        "rewards",
                        "points",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Field>
                <Field label="XP por ofício protocolado">
                  <input
                    type="number"
                    min={0}
                    value={value.rewards.xp ?? ""}
                    placeholder="Ainda não definido"
                    onChange={(e) =>
                      change(
                        "rewards",
                        "xp",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Field>
              </div>
              <p className="muted">
                A política pendente impede atribuição presumida. A alteração
                administrativa deve refletir uma decisão autorizada do
                responsável.
              </p>
            </>
          )}
          {tab === "versions" && (
            <>
              <div className="version-item">
                <strong>Versão publicada {state.published.version}</strong>
                <p>
                  {date(
                    state.published.publishedAt || state.published.createdAt,
                  )}
                </p>
              </div>
              {state.draft && (
                <div className="version-item">
                  <strong>Rascunho {state.draft.version}</strong>
                  <p>
                    Base: versão{" "}
                    {state.draft.basedOn ?? state.published.version}
                  </p>
                  <Button icon="eye" onClick={() => setModal("compare")}>
                    Comparar com publicada
                  </Button>
                </div>
              )}
              <Field label="Restaurar versão anterior">
                <select
                  value={restoreVersion}
                  onChange={(e) => setRestoreVersion(e.target.value)}
                >
                  <option value="">Selecione uma versão publicada</option>
                  {versions.map((v) => (
                    <option key={v.version} value={v.version}>
                      Versão {v.version} · {date(v.publishedAt || v.createdAt)}
                    </option>
                  ))}
                </select>
              </Field>
              <Button
                disabled={!restoreVersion || !online || !!busy}
                busy={busy === "restore"}
                onClick={() =>
                  run("restore", async () => {
                    if (
                      (dirty || advancedDirty) &&
                      !window.confirm(
                        "Há alterações ainda não salvas nesta tela. Deseja descartá-las para preparar a restauração?",
                      )
                    )
                      return;
                    await api("/config/restore", "POST", {
                      version: Number(restoreVersion),
                    });
                    await load();
                    notify(
                      "Versão anterior preparada como rascunho. Confira antes de publicar.",
                    );
                  })
                }
              >
                Preparar restauração em rascunho
              </Button>
              <p className="muted">
                Restaurar configuração não apaga documentos nem desfaz
                protocolos já realizados.
              </p>
            </>
          )}
          {tab === "advanced" && (
            <>
              <Banner kind="warn">
                Edição administrativa completa. Revise os campos e use a prévia
                antes de salvar e publicar. Credenciais não pertencem a esta
                configuração.
              </Banner>
              <Field label="Configuração em JSON">
                <textarea
                  className="json-editor"
                  rows={26}
                  spellCheck={false}
                  value={advanced}
                  onChange={(e) => {
                    setAdvanced(e.target.value);
                    setAdvancedDirty(true);
                    setDirty(true);
                    onDirty(true);
                  }}
                />
              </Field>
              {advancedError && <Banner kind="danger">{advancedError}</Banner>}
              <Button
                onClick={() => {
                  try {
                    const parsed = JSON.parse(advanced);
                    setValue(parsed);
                    setAdvancedDirty(false);
                    setDirty(true);
                    onDirty(true);
                    setAdvancedError("");
                    notify(
                      "JSON aplicado à edição local. Salve o rascunho para persistir.",
                    );
                  } catch {
                    setAdvancedError(
                      "JSON inválido. Confira chaves, aspas e vírgulas.",
                    );
                  }
                }}
              >
                Aplicar JSON à edição
              </Button>
            </>
          )}
        </section>
      </div>
      {modal === "compare" && (
        <Modal
          title="Comparação de configurações"
          wide
          onClose={() => setModal("")}
        >
          <div className="config-diff">
            <section>
              <h3>Publicada · versão {state.published.version}</h3>
              <pre>{JSON.stringify(state.published.config, null, 2)}</pre>
            </section>
            <section>
              <h3>{dirty ? "Edição nesta tela" : "Rascunho"}</h3>
              <pre>{JSON.stringify(value, null, 2)}</pre>
            </section>
          </div>
        </Modal>
      )}
      {modal === "preview" && (
        <Modal
          title="Prévia e simulação de configuração"
          wide
          onClose={() => setModal("")}
        >
          <Banner>
            Simulação local, sem consumo de API, numeração, envio, notificações
            ou recompensas. Esta prévia não comprova o funcionamento das
            integrações.
          </Banner>
          {simulation && (
            <Banner kind={simulation.valid ? "success" : "warn"}>
              {simulation.valid ? (
                "A configuração passou pela validação do servidor."
              ) : (
                <>
                  <strong>Pendências antes da publicação:</strong>
                  <ul>
                    {simulation.issues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </>
              )}
            </Banner>
          )}
          <div className="simulation-grid">
            <div>
              <h3>Redação</h3>
              <p>
                {value.ai.enabled
                  ? "Ativação proposta: habilitada"
                  : "Ativação proposta: desabilitada"}
              </p>
              <p>Modelo: {value.ai.model || "não definido"}</p>
              <p>Limite mensal: {money(value.ai.monthlyBudgetCents)}</p>
              <p>Por solicitação: {money(value.ai.maxRequestCents)}</p>
            </div>
            <div>
              <h3>Documento</h3>
              <p>Margem: {value.layout.marginMm} mm</p>
              <p>Texto: {value.layout.fontSize} pt</p>
              <p>
                Destinatários ativos:{" "}
                {value.recipients.filter((r) => r.active).length}
              </p>
            </div>
            <div>
              <h3>Protocolo</h3>
              <p>
                {value.solar.enabled
                  ? "Ativação proposta: habilitada"
                  : "Ativação proposta: desabilitada"}
              </p>
              <p>
                {value.solar.workflowVerified
                  ? "Fluxo marcado como verificado"
                  : "Fluxo real ainda não verificado"}
              </p>
              <p>
                A situação efetiva depende da conexão protegida e da verificação
                do servidor.
              </p>
            </div>
          </div>
          <div className="simulation-paper">
            <header>{value.layout.heading || "Cabeçalho configurável"}</header>
            <p>
              <strong>PRÉVIA · SEM VALIDADE OFICIAL</strong>
            </p>
            <p>
              Texto fictício para conferir a composição do documento. As
              informações e os arquivos de um caso real serão conferidos pelo
              usuário antes da finalização.
            </p>
            <footer>{value.layout.footer || "Rodapé configurável"}</footer>
          </div>
          <div className="modal-actions">
            <Button onClick={() => setModal("compare")}>
              Comparar configuração completa
            </Button>
            <Button kind="primary" onClick={() => setModal("")}>
              Voltar à edição
            </Button>
          </div>
        </Modal>
      )}
      {modal === "publish" && (
        <Modal title="Publicar configuração" onClose={() => setModal("")}>
          <p>
            Publicar o rascunho {state.draft?.version} para os usuários
            autorizados do módulo?
          </p>
          <Banner kind="warn">
            Confira ativações, custos, destinatários e mapeamentos. A publicação
            passa a orientar as próximas operações; documentos finalizados
            permanecem preservados.
          </Banner>
          <div className="modal-actions">
            <Button onClick={() => setModal("compare")}>Comparar</Button>
            <Button
              kind="primary"
              busy={busy === "publish"}
              onClick={() =>
                run("publish", async () => {
                  await api("/config/publish", "POST");
                  await load();
                  onPublished();
                  setModal("");
                  notify("Configuração publicada no servidor.");
                })
              }
            >
              Publicar versão
            </Button>
          </div>
        </Modal>
      )}
    </fieldset>
  );
}
function AssetUpload({
  label,
  value,
  online,
  onUploaded,
  onBusyChange,
  failed,
}: {
  label: string;
  value?: string;
  online: boolean;
  onUploaded: (id: string) => void;
  onBusyChange?: (busy: boolean) => void;
  failed: (e: any) => void;
}) {
  const [busy, setBusy] = useState(false),
    [uploaded, setUploaded] = useState(false),
    [previewFailedId, setPreviewFailedId] = useState<string | null>(null);
  return (
    <div className="asset-upload">
      <label className="button secondary">
        <Icon name="upload" size={17} />
        {busy ? "Recebendo imagem…" : label}
        <input
          className="sr-only"
          aria-label={label}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          disabled={!online || busy}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            setBusy(true);
            onBusyChange?.(true);
            try {
              const form = new FormData();
              form.set("file", file);
              const record = await api<FileRecord>(
                "/admin/files",
                "POST",
                form,
              );
              onUploaded(record.id);
              setUploaded(true);
            } catch (error) {
              failed(error);
            } finally {
              setBusy(false);
              onBusyChange?.(false);
            }
          }}
        />
      </label>
      {value && (
        <>
          <span>
            <Icon name="check" size={15} />
            {uploaded
              ? "Imagem recebida. Salve e publique a configuração."
              : "Imagem configurada."}
          </span>
          <figure style={{ width: "100%", margin: 0 }}>
            {previewFailedId === value ? (
              <>
                <p className="muted" role="status">
                  Não foi possível carregar a imagem configurada. Confira o
                  arquivo e seu acesso.
                </p>
                <Button onClick={() => setPreviewFailedId(null)}>
                  Tentar novamente
                </Button>
              </>
            ) : (
              <img
                key={value}
                src={fileUrl(value)}
                alt={"Prévia da " + label.replace(/^Enviar /, "")}
                onError={() => setPreviewFailedId(value)}
                style={{
                  display: "block",
                  maxWidth: "100%",
                  maxHeight: "calc(var(--space6) * 5)",
                  objectFit: "contain",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radiusSmall)",
                  background: "var(--surface)",
                }}
              />
            )}
          </figure>
        </>
      )}
    </div>
  );
}
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="toggle-row">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
      <strong>{checked ? "Ativado" : "Desativado"}</strong>
    </label>
  );
}
function NumberField({
  label,
  value,
  onChange,
  min = 0,
  max,
  step,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number | "any";
}) {
  return (
    <Field label={label}>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </Field>
  );
}
function ObjectFields({
  title,
  value,
  onChange,
  hint,
}: {
  title: string;
  value: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
  hint?: string;
}) {
  const [newKey, setNewKey] = useState("");
  return (
    <section className="object-fields">
      <h3>{title}</h3>
      {hint && <p className="muted">{hint}</p>}
      {Object.entries(value).map(([key, val]) => (
        <div className="object-row" key={key}>
          <label>
            <span>{key}</span>
            <input
              value={val}
              onChange={(e) => onChange({ ...value, [key]: e.target.value })}
            />
          </label>
          <Button
            kind="ghost"
            icon="close"
            aria-label={"Remover mapeamento " + key}
            onClick={() => {
              const next = { ...value };
              delete next[key];
              onChange(next);
            }}
          />
        </div>
      ))}
      <div className="object-add">
        <input
          aria-label={"Novo campo de " + title}
          placeholder="Identificador do campo"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
        />
        <Button
          icon="plus"
          disabled={!newKey.trim() || newKey in value}
          onClick={() => {
            onChange({ ...value, [newKey.trim()]: "" });
            setNewKey("");
          }}
        >
          Adicionar campo
        </Button>
      </div>
    </section>
  );
}
